// KOA-5857 — Discard-changes confirmation popover.
//
// Apple-style inline confirm anchored to the Discard button. Lighter
// than a full modal (modals interrupt flow even when dismissed), but
// destructive-enough to force a deliberate keystroke or click before
// pending edits are thrown away. Opens only above the Discard threshold
// or when any deletes are pending — small edits still discard without
// asking, since ⌘Z is right there.

import React, { useEffect, useRef } from "react";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";

export interface IDiscardConfirmAnchor {
  x: number;
  y: number;
}

export interface IDiscardConfirmPopoverProps {
  anchor: IDiscardConfirmAnchor;
  dirtyCount: number;
  deleteCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const DiscardConfirmPopover: React.FC<IDiscardConfirmPopoverProps> = (
  props: IDiscardConfirmPopoverProps
): JSX.Element => {
  const { anchor, dirtyCount, deleteCount, onConfirm, onCancel } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Move focus to the destructive confirm button so keyboard users land
  // on a single Enter-to-confirm path; Esc closes via the global handler
  // below. Default-focused destructive button mirrors the macOS Save
  // dialog convention where the primary destructive action is reachable
  // via a single keystroke once the dialog has the user's attention.
  useEffect((): void => {
    const node: HTMLDivElement | null = containerRef.current;
    if (node === null) {
      return;
    }
    const confirmButton: HTMLButtonElement | null = node.querySelector<HTMLButtonElement>(
      "[data-discard-confirm-action]"
    );
    if (confirmButton !== null) {
      confirmButton.focus();
    }
  }, []);

  // Close on outside-click and on Esc — Cmd+Z still resurrects via the
  // editor's own global undo handler, but inside an open confirm we want
  // Esc to mean "back out of the confirm" rather than "discard".
  useEffect((): (() => void) => {
    const handlePointer = (event: MouseEvent): void => {
      const node: HTMLDivElement | null = containerRef.current;
      if (node === null) {
        return;
      }
      if (!node.contains(event.target as Node)) {
        onCancel();
      }
    };
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey, true);
    return (): void => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey, true);
    };
  }, [onCancel]);

  const headline: string =
    dirtyCount === 1 ? "Discard 1 pending change?" : `Discard ${dirtyCount.toLocaleString("en-US")} pending changes?`;

  // Highlight deletes specifically — the user explicitly chose to remove
  // those rows, so reverting that intent feels different from reverting
  // edits. Mention undo so the user knows the option still exists if
  // they meant to.
  const subtitle: string =
    deleteCount === 0
      ? "You can still bring them back with ⌘Z afterwards."
      : deleteCount === 1
        ? "One row is marked for deletion. ⌘Z still works after discarding."
        : `${deleteCount.toLocaleString("en-US")} rows are marked for deletion. ⌘Z still works after discarding.`;

  return (
    <div
      ref={containerRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="kuika-table-editor__discard-confirm-title"
      className="kuika-table-editor__discard-confirm"
      style={{ left: anchor.x, top: anchor.y }}
    >
      <div id="kuika-table-editor__discard-confirm-title" className="kuika-table-editor__discard-confirm-title">
        {headline}
      </div>
      <div className="kuika-table-editor__discard-confirm-subtitle">{subtitle}</div>
      <div className="kuika-table-editor__discard-confirm-actions">
        <KuikaButton type="text" size="sm" onClick={onCancel}>
          Keep editing
        </KuikaButton>
        <KuikaButton
          type="primary"
          size="sm"
          color="danger"
          onClick={onConfirm}
          // Marker for the focus-on-mount effect above.
          {...({ "data-discard-confirm-action": "true" } as Record<string, string>)}
        >
          Discard
        </KuikaButton>
      </div>
    </div>
  );
};

export default DiscardConfirmPopover;
